// RTK Query slice for every TMS API v3 call; one funnel, so the BFF rework changes v3BaseQuery only.
import { createApi } from '@reduxjs/toolkit/query/react'

const v3BaseQuery = async ({ path, method = 'GET', body, relay = false, form = false }, { getState }) => {
  const { tmsApiBaseUrl, tmsApiToken, customerId, groupId, csrfToken } = getState().misc.data

  // Machine-only writes (SCOPE-7) relay through the monolith; `form` posts to a classic route, unread.
  const url = form ? path : (relay
    ? `/customers/${customerId}/groups/${groupId}/${path}`
    : `${tmsApiBaseUrl}${path}`)

  const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' }
  if (form) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded'
    // Classic actions render an HTML partial after saving, so a JSON Accept turns that render into a 500.
    headers['Accept'] = 'text/html'
    headers['X-CSRF-Token'] = csrfToken
  } else if (relay) {
    headers['X-CSRF-Token'] = csrfToken
  } else {
    if (!tmsApiToken) { return { error: { status: 401, data: {} } } }
    headers['Authorization'] = `Bearer ${tmsApiToken}`
  }

  const options = { method, headers }
  if (relay || form) { options.credentials = 'same-origin' }
  // rerank redirects to the Adjust page; following it would download that page on every reorder.
  if (form) { options.redirect = 'manual' }
  if (body !== undefined) {
    options.body = form ? new URLSearchParams(body).toString() : JSON.stringify(body)
  }

  try {
    const response = await fetch(url, options)
    // An unfollowed redirect is opaque (status 0) and means the action ran; the HTML is never parsed.
    if (form) {
      if (!response.ok && response.type !== 'opaqueredirect') {
        return { error: { status: response.status, data: {} } }
      }
      return { data: {} }
    }

    const text = response.status === 204 ? '' : await response.text()
    let data = {}
    // An expired session answers with an HTML redirect, so an unparseable body fails even on 200.
    try { data = text ? JSON.parse(text) : {} } catch (error) {
      return { error: { status: response.ok ? 401 : response.status, data: {} } }
    }
    if (!response.ok) return { error: { status: response.status, data } }
    return { data }
  } catch (error) {
    return { error: { status: 'FETCH_ERROR', error: String(error) } }
  }
}

// `silent: true` lets a bulk sequence refetch once at the end: TMS API throttles GETs at 20/min per path.
const teeSheetTags = (result, error, arg) => (arg?.silent ? [] : [ 'TeeSheet' ])

// Enrolment also changes the member search, which is cheap; only the tee sheet refetch is held back.
const memberAndTeeSheetTags = (result, error, arg) => [ 'MemberSearch', ...teeSheetTags(result, error, arg) ]

const roundPath = ({ leagueId, roundId }) => `/v3/leagues/${leagueId}/rounds/${roundId}`

// Create and update send the same spec body; JSON.stringify drops the keys left undefined.
const specBody = ({ intName, nodeSelections, divisionIds, handicapAdjustment, payout, payoutGross,
  points, pointsGross, pointsPurseMode, lookup, specialHoles, specAttrs }) => ({
  tournament_spec: {
    int_name: intName,
    division_ids: divisionIds,
    node_selections: nodeSelections,
    handicap_adjustment: handicapAdjustment,
    payout,
    payout_gross: payoutGross,
    points,
    points_gross: pointsGross,
    points_purse_mode: pointsPurseMode,
    lookup,
    special_holes: specialHoles,
    // Spec columns the panel owns under their own API names (quota_type, starting_quota).
    ...(specAttrs || {}),
  },
})

// Every tee_sheet_tools action is a POST the sheet is refetched after.
const teeSheetTool = (build, action, body) => build.mutation({
  query: ({ leagueId, roundId, ...args }) => ({
    path: `${roundPath({ leagueId, roundId })}/tee_sheet_tools/${action}`,
    method: 'POST',
    body: body(args),
  }),
  invalidatesTags: teeSheetTags,
})

// 100 is the server's max page and callers treat the list as complete, so follow the cursor.
// The Adjust screen's own endpoints: they hold the spec lock, recompute the display fields and re-cache
// the leaderboard.
const adjustAction = (build, action, body, invalidatesTags = []) => build.mutation({
  query: ({ leagueId, roundId, ...args }) => ({
    form: true,
    path: `/leagues/${leagueId}/rounds/${roundId}/v2tournaments/${action}`,
    method: 'POST',
    body: body(args),
  }),
  invalidatesTags,
})

const fetchAllPages = async (baseQuery, pathFor, key) => {
  const items = []
  for (let page = 1; page <= 20; page += 1) {
    const result = await baseQuery({ path: pathFor(`page=${page}&limit=100`) })
    if (result.error) { return result }
    items.push(...(result.data[key] || []))
    if (!result.data.links || !result.data.links.next) { break }
  }
  return { data: items }
}

export const glApi = createApi({
  reducerPath: 'glApi',
  baseQuery: v3BaseQuery,
  tagTypes: [
    'Presets', 'TournamentSpecs', 'UserTournamentSpecs', 'TeeSheet', 'Round', 'Tournaments',
    'MemberSearch', 'GroupRoster', 'UserScoredResults',
  ],
  endpoints: (build) => ({
    // ── Phase 0: Rounds ───────────────────────────────────────────────────
    listRounds: build.query({
      // limit=100 (the max): the wizard scans this list for its own round, past the default 25.
      query: (leagueId) => ({ path: `/v3/leagues/${leagueId}/rounds?limit=100` }),
      providesTags: ['Round'],
    }),

    // ── Phase 1: Games ────────────────────────────────────────────────────
    listPresets: build.query({
      query: (leagueId) => ({ path: `/v3/leagues/${leagueId}/tournament_presets` }),
      providesTags: ['Presets'],
    }),

    getSpecFormContext: build.query({
      query: ({ leagueId, roundId, intName }) => ({
        path: `${roundPath({ leagueId, roundId })}/tournament_specs/new${intName ? `?int_name=${encodeURIComponent(intName)}` : ''}`,
      }),
    }),

    getValidMagicNetworkNodes: build.query({
      query: ({ leagueId, roundId, selections = {}, context }) => {
        const parts = Object.entries(selections)
          .filter(([, v]) => v !== '' && v != null)
          .map(([k, v]) => `${encodeURIComponent(`selections[${k}]`)}=${encodeURIComponent(v)}`)
        // `context` scopes which nodes the server offers (e.g. group_event hides the
        // formats/competitions a group leader can't use); omitted = the full network.
        if (context) { parts.push(`context=${encodeURIComponent(context)}`) }
        const qs = parts.join('&')
        const base = roundId
          ? `/v3/leagues/${leagueId}/rounds/${roundId}/magic_network/valid_nodes`
          : `/v3/leagues/${leagueId}/magic_network/valid_nodes`
        return { path: qs ? `${base}?${qs}` : base }
      },
    }),

    listTournamentSpecs: build.query({
      query: (round) => ({ path: `${roundPath(round)}/tournament_specs` }),
      providesTags: ['TournamentSpecs'],
    }),

    // Through the relay like the writes, so the add-ons are read and written in one place.
    listUserTournamentSpecs: build.query({
      query: ({ leagueId, roundId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/user_scored_specs?league_id=${leagueId}`,
      }),
      providesTags: ['UserTournamentSpecs'],
    }),

    // Spec writes relay so the monolith's Tournaments2::Spec callbacks run; the list stays on v3.
    createTournamentSpec: build.mutation({
      query: ({ leagueId, roundId, ...spec }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/tournament_specs`,
        method: 'POST',
        body: { league_id: leagueId, ...specBody(spec) },
      }),
      invalidatesTags: ['TournamentSpecs', 'Tournaments'],
    }),

    updateTournamentSpec: build.mutation({
      query: ({ leagueId, roundId, specId, ...spec }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/tournament_specs/${specId}`,
        method: 'PATCH',
        body: { league_id: leagueId, ...specBody(spec) },
      }),
      invalidatesTags: ['TournamentSpecs'],
    }),

    // league_id in the query string: proxies may drop a DELETE body and the relay would answer 400.
    deleteTournamentSpec: build.mutation({
      query: ({ leagueId, roundId, specId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/tournament_specs/${specId}?league_id=${leagueId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TournamentSpecs'],
    }),

    // ── Phase 2: Pairings ─────────────────────────────────────────────────
    memberSearch: build.query({
      query: ({ leagueId, roundId, q = '' }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_enrollment/member_search?q=${encodeURIComponent(q)}`,
      }),
      providesTags: ['MemberSearch'],
    }),

    checkRegime: build.mutation({
      query: ({ leagueId, roundId, nodeSelections }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_enrollment/check_regime`,
        method: 'POST',
        body: { node_selections: nodeSelections },
      }),
    }),

    // The group's own roster, not member_search (which falls through to the master roster).
    listGroupRoster: build.query({
      query: ({ page = 1, filterLetters = '' }) => ({
        path: `members.json?page=${page}`
          + (filterLetters ? `&filter_letters=${encodeURIComponent(filterLetters)}` : ''),
        relay: true,
      }),
      providesTags: ['GroupRoster'],
    }),

    // The customer_group filter only narrows the list (v3 has no group resource); paged, or newest events drop.
    listGroupEvents: build.query({
      async queryFn({ groupId }, _api, _extra, baseQuery) {
        const pathFor = (page) => `/v3/leagues?customer_group=${encodeURIComponent(groupId)}&archived=false&${page}`
        const result = await fetchAllPages(baseQuery, pathFor, 'data')
        return result.error ? result : { data: { data: result.data } }
      },
    }),

    // One past event's members, not its players, so a golfer never placed on its tee sheet still comes across.
    listEventRoster: build.query({
      query: ({ leagueId, page = 1 }) => ({
        path: `/v3/leagues/${leagueId}/roster_tools/event_roster?page=${page}&limit=100`,
      }),
    }),

    // The monolith enrols, so the import runs its entry numbers, Players and accept. Answers 202 { job_id },
    // polled on reorganizeStatus; the tags are invalidated once the job is done, not here.
    importMembers: build.mutation({
      query: ({ leagueId, roundId, memberIds }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/import_members`,
        method: 'POST',
        body: { league_id: leagueId, member_ids: memberIds },
      }),
    }),

    importMember: build.mutation({
      query: ({ leagueId, roundId, memberId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/import_member`,
        method: 'POST',
        body: { league_id: leagueId, member_id: memberId },
      }),
      invalidatesTags: memberAndTeeSheetTags,
    }),

    // The index goes as typed, so Member#usga_index= reads the sign; custom fields use logical keys.
    createMember: build.mutation({
      query: ({ leagueId, roundId, firstName, lastName, email, handicapIndex, ghin, customFields }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/create_member`,
        method: 'POST',
        body: {
          league_id: leagueId,
          first_name: firstName,
          last_name: lastName,
          ...(email ? { email } : {}),
          ...(handicapIndex ? { usga_index: handicapIndex } : {}),
          ...(ghin ? { handicap_network_id: ghin } : {}),
          ...(customFields && Object.keys(customFields).length ? { custom_fields: customFields } : {}),
        },
      }),
      invalidatesTags: [ 'TeeSheet', 'MemberSearch' ],
    }),

    enrollInSkins: build.mutation({
      query: ({ leagueId, roundId, memberId }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_enrollment/enroll_in_skins`,
        method: 'POST',
        body: { member_id: memberId },
      }),
    }),

    enrollRosterInSkins: build.mutation({
      query: ({ leagueId, roundId }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_enrollment/enroll_roster_in_skins`,
        method: 'POST',
        body: {},
      }),
    }),

    // user_tournament_specs create/destroy are machine-only (SCOPE-7), so they relay.
    createUserScoredSpec: build.mutation({
      query: ({ leagueId, roundId, name }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/user_scored_specs`,
        method: 'POST',
        body: { league_id: leagueId, name },
      }),
      invalidatesTags: ['UserTournamentSpecs'],
    }),

    // A real rename through the relay, so a failure can no longer destroy the game mid-way.
    updateUserScoredSpec: build.mutation({
      query: ({ leagueId, roundId, specId, name }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/user_scored_specs/${specId}`,
        method: 'PATCH',
        body: { league_id: leagueId, name },
      }),
      invalidatesTags: [ 'UserTournamentSpecs' ],
    }),

    // league_id in the query string: proxies may drop a DELETE body and the relay would answer 400.
    deleteUserScoredSpec: build.mutation({
      query: ({ leagueId, roundId, specId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/user_scored_specs/${specId}?league_id=${leagueId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserTournamentSpecs'],
    }),

    // The monolith owns the messaging, so this relays. Returns { sent: {emails, texts, recipients} }.
    sendScoringLinks: build.mutation({
      query: ({ leagueId, roundId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/send_scoring_links`,
        method: 'POST',
        body: { league_id: leagueId },
      }),
    }),

    createRow: teeSheetTool(build, 'create_row', ({ playerIds = [] }) => ({ player_ids: playerIds })),

    // limit=100 (the server's max): the wizard treats this as the whole sheet, so 25 skipped rows.
    getTeeSheet: build.query({
      query: ({ leagueId, roundId }) => ({
        path: `${roundPath({ leagueId, roundId })}/tee_sheet`
          + '?include_empty=true&include_all_custom_fields=true&limit=100',
      }),
      providesTags: ['TeeSheet'],
    }),

    getUnplacedPlayers: build.query({
      async queryFn(round, _api, _extra, baseQuery) {
        const pathFor = (page) =>
          `${roundPath(round)}/round_enrollment/unplaced_players?${page}&include_all_custom_fields=true`
        const result = await fetchAllPages(baseQuery, pathFor, 'players')
        return result.error ? result : { data: { players: result.data, count: result.data.length } }
      },
      providesTags: ['TeeSheet'],
    }),

    addPlayers: teeSheetTool(build, 'add_players', ({ foursomeId, playerIds, positions }) => ({
      foursome_id: foursomeId,
      player_ids: playerIds,
      ...(positions?.length ? { positions } : {}),
    })),

    // keepEmptyFoursomes: removing a group's last golfer leaves its now-empty row instead of deleting it.
    removePlayers: teeSheetTool(build, 'remove_players', ({ playerIds, keepEmptyFoursomes }) => ({
      player_ids: playerIds,
      ...(keepEmptyFoursomes ? { keep_empty_foursomes: true } : {}),
    })),

    swapPlayers: teeSheetTool(build, 'swap_players', ({ playerAId, playerBId }) => ({
      player_a_id: playerAId,
      player_b_id: playerBId,
    })),

    addBlindDraw: teeSheetTool(build, 'add_blinds', ({ foursomeId, sourcePlayerId, position }) => ({
      foursome_id: foursomeId,
      source_player_id: sourcePlayerId,
      ...(position != null && position >= 1 ? { position } : {}),
    })),

    removeBlindDraw: teeSheetTool(build, 'remove_blind', ({ blindId }) => ({ blind_id: blindId })),

    // Runs the scheduler as a relayed job ({ job_id }); the caller polls and refetches the sheet.
    reorganizePlayers: build.mutation({
      query: ({ leagueId, roundId, schedulingOption, optimization, metadatumId,
                consolidateCustomFieldFoursomes, includeAttendingCustomField }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/reorganize_players`,
        method: 'POST',
        body: {
          league_id: leagueId,
          scheduling_option: schedulingOption,
          ...(optimization != null ? { optimization } : {}),
          ...(metadatumId != null ? { metadatum_id: metadatumId } : {}),
          ...(consolidateCustomFieldFoursomes != null
            ? { consolidate_custom_field_foursomes: consolidateCustomFieldFoursomes } : {}),
          ...(includeAttendingCustomField != null
            ? { include_attending_custom_field: includeAttendingCustomField } : {}),
        },
      }),
    }),

    // Polls the reorganize job through the relay; league_id rides in the query string of the GET.
    reorganizeStatus: build.query({
      query: ({ leagueId, roundId, jobId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/reorganize_status/${jobId}?league_id=${leagueId}`,
      }),
    }),

    getLeagueCustomFields: build.query({
      query: (round) => ({ path: `${roundPath(round)}/tee_sheet_tools/league_custom_fields` }),
    }),

    reorganize: teeSheetTool(build, 'reorganize', ({ foursomeIds }) => ({ foursome_ids: foursomeIds })),

    deleteFoursome: teeSheetTool(build, 'delete_foursome', ({ foursomeId }) => ({ foursome_id: foursomeId })),

    // `foursomeIds` puts one time on many rows in one request; `foursomeId` stays for single-row callers.
    setTeeTime: teeSheetTool(build, 'set_tee_time', ({ foursomeId, foursomeIds, teeTime }) => ({
      foursome_ids: foursomeIds?.length ? foursomeIds : [foursomeId],
      tee_time: teeTime,
    })),

    listLeagueCourses: build.query({
      query: (leagueId) => ({ path: `/v3/leagues/${leagueId}/courses` }),
    }),

    setTeeTimesWithInterval: teeSheetTool(build, 'set_tee_times_with_interval', (
      { startTime, intervalMinutes, foursomeIds, extraTimeGroupSize, extraTimeAmount },
    ) => ({
      start_time: startTime,
      interval_minutes: intervalMinutes,
      ...(foursomeIds?.length ? { foursome_ids: foursomeIds } : {}),
      ...(extraTimeGroupSize ? { extra_time_group_size: extraTimeGroupSize } : {}),
      ...(extraTimeAmount ? { extra_time_amount: extraTimeAmount } : {}),
    })),

    setShotgun: teeSheetTool(build, 'set_shotgun', ({ teeTime, startingHole, reverseShotgun, foursomeIds }) => ({
      tee_time: teeTime,
      starting_hole: startingHole,
      reverse_shotgun: reverseShotgun,
      ...(foursomeIds?.length ? { foursome_ids: foursomeIds } : {}),
    })),

    setHole: teeSheetTool(build, 'set_hole', ({ hole, holeLetter, foursomeIds }) => ({
      hole,
      ...(holeLetter ? { hole_letter: holeLetter } : {}),
      ...(foursomeIds?.length ? { foursome_ids: foursomeIds } : {}),
    })),

    // holesType is the endpoint's own vocabulary ('All 18', 'Front 9', ...), resolved to a virtual tee server-side.
    setCourseAndTee: teeSheetTool(build, 'set_course_and_tee', ({ teeId, playerIds, holesType }) => ({
      tee_id: teeId,
      ...(playerIds?.length ? { player_ids: playerIds } : {}),
      ...(holesType ? { holes_type: holesType } : {}),
    })),

    updateMember: build.mutation({
      // Machine-only (SCOPE-7), so it relays; the fields go flat beside league_id (RTM-10063).
      query: ({ leagueId, memberId, member }) => ({
        relay: true,
        path: `tms_relay/members/${memberId}`,
        method: 'PATCH',
        body: { league_id: leagueId, ...member },
      }),
      invalidatesTags: ['TeeSheet'],
    }),

    removeMember: build.mutation({
      query: ({ leagueId, roundId, playerRoundId }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_enrollment/remove_member`,
        method: 'DELETE',
        body: { player_round_id: playerRoundId },
      }),
      invalidatesTags: ['TeeSheet', 'MemberSearch'],
    }),

    updateRoundExpressEventSettings: build.mutation({
      // eslint-disable-next-line camelcase
      query: ({ leagueId, roundId, foursome_count, allow_players_to_choose_tee_time, eventWithNoTeesheet }) => ({
        path: `${roundPath({ leagueId, roundId })}/round_express_event_settings`,
        method: 'PATCH',
        // event_with_no_teesheet: the default course, tees and hole a walk-on event scores against.
        // eslint-disable-next-line camelcase
        body: { foursome_count, allow_players_to_choose_tee_time, event_with_no_teesheet: eventWithNoTeesheet },
      }),
      invalidatesTags: ['Round'],
    }),

    // REACT-045: the URL comes from Rails, not assembled here. A monolith route, not
    // TMS API v3, so it bypasses v3BaseQuery and carries the CSRF token, not a bearer.
    toggleTournamentStatus: build.mutation({
      queryFn: async ({ statusUrl, leagueId, status, csrfToken }) => {
        try {
          const res = await fetch(
            statusUrl,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
              body: JSON.stringify({ league_id: leagueId, status }),
            },
          )
          const data = res.status === 204 ? {} : await res.json()
          if (!res.ok) return { error: { status: res.status, data } }
          return { data }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      invalidatesTags: ['Round'],
    }),

    // The round's scores-in count; POST is the API's own shape for this read.
    getRoundInformation: build.query({
      query: (round) => ({
        path: `${roundPath(round)}/round_tools/round_information`,
        method: 'POST',
      }),
      providesTags: [ 'Round' ],
    }),

    // Rows for games scored by hand; the widget serves display strings keyed on aggregates instead.
    listUserScoredResults: build.query({
      query: ({ leagueId, roundId }) => ({
        relay: true,
        path: `tms_relay/rounds/${roundId}/user_scored_results?league_id=${leagueId}`,
      }),
      providesTags: [ 'UserScoredResults' ],
    }),

    adjustUserScoredResult: adjustAction(build, 'adjust_result', ({ resultId, updateType, newValue }) => (
      { result_id: resultId, update_type: updateType, new_value: newValue }
    )),

    clearUserScoredResult: adjustAction(
      build, 'clean_result', ({ aggregateId }) => ({ aggregate_id: aggregateId }), [ 'UserScoredResults' ],
    ),

    // Top-level member route keyed on the event, not nested under league and round.
    rerankUserScoredEvent: build.mutation({
      query: ({ eventId }) => ({
        form: true,
        path: `/v2tournaments/${eventId}/rerank`,
      }),
      invalidatesTags: [ 'UserScoredResults' ],
    }),

    getTournamentResultsWidget: build.query({
      query: ({ leagueId, roundId }) => ({
        path: `/v3/leagues/${leagueId}/widgets/tournament_results?round_id=${roundId}`,
      }),
      providesTags: ['Tournaments'],
    }),
  }),
})

export const {
  useListRoundsQuery,
  useGetRoundInformationQuery,
  useListPresetsQuery,
  useGetSpecFormContextQuery,
  useGetValidMagicNetworkNodesQuery,
  useListTournamentSpecsQuery,
  useListUserTournamentSpecsQuery,
  useCreateTournamentSpecMutation,
  useUpdateTournamentSpecMutation,
  useDeleteTournamentSpecMutation,
  useMemberSearchQuery,
  useCheckRegimeMutation,
  useImportMemberMutation,
  useListGroupRosterQuery,
  useListGroupEventsQuery,
  useListEventRosterQuery,
  useImportMembersMutation,
  useCreateMemberMutation,
  useGetTeeSheetQuery,
  useGetUnplacedPlayersQuery,
  useAddPlayersMutation,
  useRemovePlayersMutation,
  useSwapPlayersMutation,
  useAddBlindDrawMutation,
  useRemoveBlindDrawMutation,
  useCreateRowMutation,
  useReorganizeMutation,
  useDeleteFoursomeMutation,
  useSetTeeTimeMutation,
  useSetTeeTimesWithIntervalMutation,
  useSetShotgunMutation,
  useSetHoleMutation,
  useSetCourseAndTeeMutation,
  useToggleTournamentStatusMutation,
  useListLeagueCoursesQuery,
  useListUserScoredResultsQuery,
  useAdjustUserScoredResultMutation,
  useClearUserScoredResultMutation,
  useRerankUserScoredEventMutation,
  useGetTournamentResultsWidgetQuery,
  useRemoveMemberMutation,
  useUpdateRoundExpressEventSettingsMutation,
  useEnrollInSkinsMutation,
  useEnrollRosterInSkinsMutation,
  useUpdateMemberMutation,
  useReorganizePlayersMutation,
  useLazyReorganizeStatusQuery,
  useGetLeagueCustomFieldsQuery,
  useCreateUserScoredSpecMutation,
  useSendScoringLinksMutation,
  useUpdateUserScoredSpecMutation,
  useDeleteUserScoredSpecMutation,
} = glApi
